/*
 * Copyright (c) Airbus SAS. All rights reserved.
 */

import { hasClass } from '@/js/utils/css'
import { findAll, findOne } from '@/js/utils/dom'

/**
 * Mirror a select/checkbox value to a grid of `.card` elements.
 *
 * @type {Listener}
 */
export default {
    selector: 'select[data-toggle="card"],input[data-toggle="card"]',
    connect(element) {
        const isSelectItem = element.tagName === 'SELECT'
        const isMultipleValuesAllowed = isSelectItem ? element.multiple : Boolean(element.dataset.multiple)
        const cardsContainer = findOne(element.dataset.target)
        if (!cardsContainer) {
            return
        }

        const cards = [...findAll('.card', cardsContainer)]
        updateCards()

        element.addEventListener('card.refresh', updateCards)

        const cardHandlers = cards.map((card) => {
            const onClick = () => {
                if (!isMultipleValuesAllowed && !hasClass(card, 'selected')) {
                    cards
                        .filter((other) => hasClass(other, 'selected'))
                        .forEach((other) => other.classList.remove('selected'))
                }

                card.classList.toggle('selected')

                updateItemValue()
            }
            card.addEventListener('click', onClick)

            return [card, onClick]
        })

        function updateCards() {
            let selectedValues = getItemValue()

            if (!isSelectItem && isMultipleValuesAllowed) {
                selectedValues = selectedValues.split(',')
            }

            if (!Array.isArray(selectedValues)) {
                selectedValues = [selectedValues]
            }

            cards.forEach((card) => {
                card.classList.toggle('selected', selectedValues.includes(String(card.dataset.value)))
            })
        }

        function updateItemValue() {
            const selectedCardValues = cards
                .filter((card) => hasClass(card, 'selected'))
                .map((card) => String(card.dataset.value))

            if (isSelectItem) {
                const values = selectedCardValues.length > 0 ? selectedCardValues : ['']
                Array.from(element.options).forEach((option) => {
                    option.selected = values.includes(option.value)
                })
            } else if (isMultipleValuesAllowed) {
                element.value = selectedCardValues.join(',')
            } else {
                element.value = selectedCardValues.length > 0 ? selectedCardValues[0] : ''
            }

            element.dispatchEvent(new Event('change', { bubbles: true }))
        }

        function getItemValue() {
            if (isSelectItem && element.multiple) {
                return Array.from(element.selectedOptions, (option) => option.value)
            }

            return element.value
        }

        return () => {
            element.removeEventListener('card.refresh', updateCards)
            cardHandlers.forEach(([card, onClick]) => card.removeEventListener('click', onClick))
        }
    },
}
